<template>
    <div class="wine-release-landing" :class="{ 'is-ready': is_ready }">
        <branding-header />

        <div class="hero-section">
            <div class="release-badge">
                <v-icon color="amber darken-2" small>mdi-star</v-icon>
                <span>{{ wine_data.release_type }}</span>
            </div>

            <h1 class="wine-title">{{ wine_data.name }}</h1>

            <img class="wine-image" height="185" src="@/assets/barrel-creek-2024-sauv-blanc.png" alt="Lifestyle image of Barrel Creek 2024 Sauvignon Blanc" />
        </div>

        <div class="tasting-notes">
            <h3>Tasting Notes</h3>
            <p>{{ wine_data.tasting_notes }}</p>
        </div>

        <div class="pricing-section">
            <div class="price-comparison">
                <div class="regular-price">
                    <span class="label">Regular Price</span>
                    <span class="price strikethrough">${{ wine_data.regular_price.toFixed(2) }}</span>
                </div>
                <div class="club-price">
                    <span class="label">Your Club Price</span>
                    <span class="price highlight">${{ wine_data.club_price.toFixed(2) }}</span>
                </div>
            </div>
            <p class="savings-note">
                Save ${{ (wine_data.regular_price - wine_data.club_price).toFixed(2) }} per bottle!
            </p>
            <p class="allocation-note">
                <v-icon small color="primary">mdi-information</v-icon>
                Up to {{ wine_data.max_allocation }} bottles per member
            </p>
        </div>

        <div class="chat-section">
            <embedded-chat-interface
                :customer_data="customer_data"
                :wine_data="wine_data"
                @complete="$emit('chat-complete')"
                @handoff="$emit('chat-handoff', $event)"
                @reset-web-experience="$emit('reset-web-experience')"
            />
        </div>
    </div>
</template>

<script>
import BrandingHeader from '@/components/demos/shared/branding-header.vue';
import EmbeddedChatInterface from './embedded-chat-interface.vue';

export default {
    name: 'MockWineReleaseLanding',

    components: {
        BrandingHeader,
        EmbeddedChatInterface
    },

    props: {
        wine_data: {
            type: Object,
            required: true
        },
        customer_data: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            is_ready: false
        }
    },

    mounted() {
        requestAnimationFrame(() => {
            this.is_ready = true;
        });
    }
}
</script>

<style lang="scss" scoped>
.wine-release-landing {
    --brand-primary: #40728F;
    --brand-primary-dark: #305060;
    --brand-primary-light: #6090B0;
    --wine-primary: #5d4037;
    --wine-primary-dark: #3e2723;
    --wine-primary-light: #8d6e63;
    --wine-accent: #ffd54f;
    --wine-bg: #f8f5f0;
    --wine-bg-warm: #f5f0e8;

    padding: 0.5rem;
    background: var(--wine-bg);
    min-height: 100%;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;

    &.is-ready {
        opacity: 1;
    }
}

.hero-section {
    text-align: center;
    margin-bottom: 1rem;
}

.release-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    background: linear-gradient(135deg, var(--wine-primary) 0%, var(--wine-primary-dark) 100%);
    color: var(--wine-accent);
    padding: 0.35rem 0.75rem;
    border-radius: 1rem;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.75rem;

    span {
        padding-top: 2px;
    }
}

.wine {
    &-title {
        font-family: 'Georgia', serif;
        font-size: 1.4rem;
        color: var(--wine-primary-dark);
        margin-bottom: 1rem;
        line-height: 1.2;
    }

    &-image {
        max-width: 200px;
        object-fit: contain;
        margin: 0 auto;
    }
}

.tasting-notes {
    background: white;
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);

    h3 {
        font-size: 0.85rem;
        color: var(--wine-primary);
        margin-bottom: 0.25rem;
        font-weight: 600;
    }

    p {
        font-size: 0.8rem;
        color: #666;
        line-height: 1.5;
        margin: 0;
    }
}

.pricing-section {
    background: white;
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    text-align: center;
}

.price-comparison {
    display: flex;
    justify-content: center;
    gap: 1.5rem;
    margin-bottom: 0.5rem;
}

.regular-price, .club-price {
    display: flex;
    flex-direction: column;

    .label {
        font-size: 0.7rem;
        color: #999;
        text-transform: uppercase;
    }

    .price {
        font-size: 1.25rem;
        font-weight: 700;
    }
}

.regular-price .strikethrough {
    color: #999;
    text-decoration: line-through;
}

.club-price .highlight {
    color: var(--v-success-darken1);
}

.savings-note {
    color: var(--v-success-darken1);
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
}

.allocation-note {
    font-size: 0.75rem;
    color: #666;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    margin: 0;
}

.chat-section {
    margin-bottom: 1.5rem;
}
</style>
